import * as Yup from "yup";
import moment from "moment";
import "moment-lunar";
import { useState, useEffect, useLayoutEffect } from "react";
import { useSelector } from "react-redux";
import { API_URL, GENDER_OPTIONS } from "../../../constants";
import { useFormikContext } from "formik";
import Grid from "@material-ui/core/Grid";
import Card from "@material-ui/core/Card";
import CardContent from "@material-ui/core/CardContent";
import Typography from "@material-ui/core/Typography";
import { FormikDialog } from "../../../components/dialogs";
import FormikReferenceField from "../../../components/inputs/FormikReferenceField";
import FormikDateField from "../../../components/inputs/FormikDateField";
import FormikSelectField from "../../../components/inputs/FormikSelectField";
import FormikTextField from "../../../components/inputs/FormikTextField";
import FormikHKIDField from "../../../components/inputs/FormikHKIDField";
import axios from "axios";
import { FormattedMessage } from "react-intl";

export const initialValues = {
  lastnameTc: "",
  lastname: "",
  firstnameTc: "",
  firstname: "",
  gender: null,
  dateOfBirth: "",
  hkid: "",
  contactName: "",
  contactNo: "",
  contactRelationship: "",
  estimatedCheckInDate: "",
  checkInDate: moment(new Date()).toISOString(),
  bed: "",
  residentBedType: "",
};

var today = new Date();

export const validationSchema = Yup.object().shape({
  residentBedType: Yup.string().required(
    <FormattedMessage id="validation.message.required" />
  ),
  checkInDate: Yup.date()
    .required(<FormattedMessage id="validation.message.required" />)
    .typeError(<FormattedMessage id="validation.message.invalidDateFormat" />)
    .nullable()
    .max(today, "日期必須為今日或以前"),
  bed: Yup.string().required(
    <FormattedMessage id="validation.message.required" />
  ),
});

export function CheckInDialog(props) {
  const { entity, open, onClose, onSubmit, apiUrl, normalizeReceivedValues } =
    props;
  const [entityValues, setEntityValues] = useState();
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (entity) {
      axios
        .get((apiUrl.getOne ? apiUrl.getOne : apiUrl) + "/" + entity.id)
        .then((response) => {
          let data = response?.data;
          let entityValues = { ...initialValues, ...data };
          normalizeReceivedValues(entityValues);
          setEntityValues(entityValues);
        })
        .catch((error) => {})
        .finally(() => {
          setLoaded(true);
        });
    } else {
      setEntityValues(initialValues);
    }
  }, [entity]);

  return (
    <FormikDialog
      open={open}
      entity={entity}
      dialogTitle={"入住登記 (" + entityValues?.refNo + ")"}
      onSubmit={onSubmit}
      onClose={onClose}
      button={"入住登記"}
      validationSchema={validationSchema}
    >
      <CheckInForm entity={entityValues} />
    </FormikDialog>
  );
}

const CheckInForm = (props) => {
  const home = useSelector((state) => state.home);
  const { entity, apiUrl = "" } = props;
  const { initialValues, values, touched, errors, setFieldValue, resetForm } =
    useFormikContext();

  const setLunarDateOfBirthAndAge = (value) => {
    setFieldValue(
      "lunarDateOfBirth",
      value ? moment(value).lunar().format("YYYY-MM-DD") : ""
    );
    setFieldValue("age", moment().diff(value, "years", false) || "0");
  };

  const handleDateOfBirthChange = (value) => {
    setFieldValue("dateOfBirth", moment(value).format("YYYY-MM-DD"));
    setLunarDateOfBirthAndAge(value);
  };

  useLayoutEffect(() => {
    resetForm({ values: initialValues });
    if (entity) {
      resetForm({ values: { ...initialValues, ...entity } });
      setLunarDateOfBirthAndAge(entity.dateOfBirth);
    } else {
      resetForm({ values: initialValues });
    }
  }, [entity]);

  useEffect(() => {}, []);

  return (
    <form>
      <Grid container spacing={2}>
        <Grid item sm={8} xs={12}>
          <Card variant="outlined" style={{ marginBottom: 15 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                個人資料
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="中文姓氏"
                    name="lastnameTc"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="中文名字"
                    name="firstnameTc"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="英文姓氏"
                    name="lastname"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="英文名字"
                    name="firstname"
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormikSelectField
                    disabled
                    required
                    label="性別"
                    name="gender"
                    options={GENDER_OPTIONS}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormikHKIDField disabled label="身份證號碼" name="hkid" />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormikTextField
                    disabled
                    label="其他證明文件號碼"
                    name="homeReturnPermitId"
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormikDateField
                    disabled
                    required
                    label="出生日期"
                    name="dateOfBirth"
                    onChange={handleDateOfBirthChange}
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormikDateField
                    required
                    disabled
                    label="農曆"
                    name="lunarDateOfBirth"
                  />
                </Grid>
                <Grid item sm={4} xs={12}>
                  <FormikTextField required disabled label="年齡" name="age" />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
          <Card variant="outlined">
            <CardContent>
              <Typography variant="h6" gutterBottom>
                床位資料
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={12} xs={12}>
                  <FormikReferenceField
                    edit={entity ? true : false}
                    disabled
                    required
                    label="床號"
                    name="bed"
                    apiUrl={API_URL.BED + "/references"}
                    apiParams={{
                      homeId: home?.home.id,
                      status: "AVAILABLE",
                    }}
                    getOptionLabel={(option) => option?.bedNo}
                  />
                </Grid>
                <Grid item sm={12} xs={12}>
                  <FormikReferenceField
                    edit={entity ? true : false}
                    required
                    label="床位類別"
                    name="residentBedType"
                    apiUrl={"/api/residentBedTypes"}
                    getOptionLabel={(option) => option.type}
                    apiParams={{
                      unpaged: true,
                    }}
                  />
                </Grid>
                <Grid item sm={6} xs={6}>
                  <FormikDateField
                    startTime
                    disabled
                    required
                    label="預計入住日期"
                    name="estimatedCheckInDate"
                  />
                </Grid>
                <Grid item sm={6} xs={6}>
                  <FormikDateField
                    startTime
                    required
                    label="實際入住日期"
                    name="checkInDate"
                    helperText="日期必須為今日或以前"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
        <Grid item sm={4} xs={12}>
          <Card variant="outlined" style={{ marginBottom: 15 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                聯絡人資料
              </Typography>
              <Grid container spacing={2}>
                <Grid item sm={12} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="聯絡人"
                    name="contactName"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    required
                    label="聯絡人電話"
                    name="contactNo"
                  />
                </Grid>
                <Grid item sm={6} xs={12}>
                  <FormikTextField
                    disabled
                    label="關係"
                    name="contactRelationship"
                  />
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </form>
  );
};
